import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { loadEmojiData, searchEmoji, exactEmoji, type EmojiMatch } from '../lib/emojiSearch';

// Type ":" and a word in a text box to get emoji suggestions, like Slack and Discord.
// Arrow keys and Enter or Tab pick on a keyboard; on phones a tap picks and the
// keyboard stays open. ":heart:" with a closing colon turns into ❤️ right away.
// Works on controlled and plain text boxes: it edits the text like typing does,
// so the box's own input handlers run and Cmd/Ctrl + Z still works.

type Field = HTMLTextAreaElement | HTMLInputElement;
type Open = { items: EmojiMatch[]; active: number; start: number; end: number };

// ":" at the start or after a space, then 2+ letters, right before the caret.
// So "10:30" and links stay as they are.
const OPEN_RE = /(?:^|\s):([a-z0-9_+-]{2,30})$/i;
const CLOSED_RE = /(?:^|\s):([a-z0-9_+-]{2,30}):$/i;
const LIST_ID = 'emoji-shortcodes';

function insert(el: Field, start: number, end: number, text: string) {
  el.focus();
  el.setSelectionRange(start, end);
  // execCommand keeps undo and fires a real input event; setRangeText is the fallback.
  if (!document.execCommand('insertText', false, text)) {
    el.setRangeText(text, start, end, 'end');
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

export default function EmojiShortcodes({ target, isNight }: { target: RefObject<Field | null>; isNight: boolean }) {
  const [open, setOpen] = useState<Open | null>(null);
  const [pos, setPos] = useState<{ left: number; width: number; top?: number; bottom?: number } | null>(null);
  const openRef = useRef<Open | null>(null);
  useLayoutEffect(() => { openRef.current = open; }, [open]);

  useEffect(() => {
    const el = target.current;
    if (!el) return;
    let dismissedAt = -1;   // Esc closes the list for this ":" word only
    let seq = 0;

    const pick = (emoji: string, o: Open, space: boolean) => {
      const next = el.value.charAt(o.end);
      insert(el, o.start, o.end, emoji + (space && next !== ' ' ? ' ' : ''));
      setOpen(null);
    };

    const check = async () => {
      const caret = el.selectionStart ?? 0;
      if (caret !== el.selectionEnd) { setOpen(null); return; }
      const before = el.value.slice(0, caret);
      const closed = CLOSED_RE.exec(before);
      const m = closed ?? OPEN_RE.exec(before);
      if (!m) { setOpen(null); return; }
      const start = caret - m[1].length - (closed ? 2 : 1);
      if (start === dismissedAt && !closed) return;
      const mine = ++seq;
      const all = await loadEmojiData();
      if (mine !== seq) return;   // a newer key press already ran
      if (closed) {
        const emoji = exactEmoji(all, m[1]);
        if (emoji) insert(el, start, caret, emoji);
        setOpen(null);
        return;
      }
      const items = searchEmoji(all, m[1]);
      setOpen(items.length ? { items, active: 0, start, end: caret } : null);
    };

    const onInput = (e: Event) => { if (!(e as InputEvent).isComposing) check(); };
    const onKeyDown = (e: Event) => {
      const k = e as KeyboardEvent, o = openRef.current;
      if (!o || k.isComposing) return;
      let handled = true;
      if (k.key === 'ArrowDown') setOpen({ ...o, active: (o.active + 1) % o.items.length });
      else if (k.key === 'ArrowUp') setOpen({ ...o, active: (o.active - 1 + o.items.length) % o.items.length });
      else if ((k.key === 'Enter' && !k.shiftKey) || k.key === 'Tab') pick(o.items[o.active].emoji, o, true);
      else if (k.key === 'Escape') { dismissedAt = o.start; setOpen(null); }
      else handled = false;
      // Stop here, so Enter doesn't also send and Esc doesn't also close the chat or card.
      if (handled) { k.preventDefault(); k.stopPropagation(); }
    };
    // The caret moved without typing (a click, or Left/Right/Home/End): close the list
    // if it left the ":" word. Up and Down move in the list, so they don't count.
    const onCaret = (e: Event) => {
      const key = (e as KeyboardEvent).key;
      if (openRef.current && (!key || ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key))) check();
    };
    const onBlur = () => setOpen(null);
    const onPick = (e: Event) => {
      const { emoji } = (e as CustomEvent<{ emoji: string }>).detail;
      if (openRef.current) pick(emoji, openRef.current, true);
    };

    el.addEventListener('input', onInput);
    el.addEventListener('keydown', onKeyDown);
    el.addEventListener('click', onCaret);
    el.addEventListener('keyup', onCaret);
    el.addEventListener('blur', onBlur);
    el.addEventListener('emoji-shortcode-pick', onPick);
    return () => {
      el.removeEventListener('input', onInput);
      el.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('click', onCaret);
      el.removeEventListener('keyup', onCaret);
      el.removeEventListener('blur', onBlur);
      el.removeEventListener('emoji-shortcode-pick', onPick);
      el.removeAttribute('aria-activedescendant');
    };
  }, [target]);

  // Screen readers: the text box says a list is open and which emoji is chosen.
  useEffect(() => {
    const el = target.current;
    if (!el) return;
    el.setAttribute('aria-autocomplete', 'list');
    el.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) { el.setAttribute('aria-controls', LIST_ID); el.setAttribute('aria-activedescendant', `${LIST_ID}-${open.active}`); }
    else el.removeAttribute('aria-activedescendant');
  }, [open, target]);

  // Sit just above the text box (below it when there's no room above), and follow
  // it when the page scrolls or the phone keyboard opens.
  const isOpen = !!open;
  useLayoutEffect(() => {
    const el = target.current;
    if (!isOpen || !el) return;
    const place = () => {
      const r = el.getBoundingClientRect();
      const width = Math.min(Math.max(r.width, 240), 340, window.innerWidth - 16);
      const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
      setPos(r.top > 250 ? { left, width, bottom: window.innerHeight - r.top + 6 } : { left, width, top: r.bottom + 6 });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    window.visualViewport?.addEventListener('resize', place);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      window.visualViewport?.removeEventListener('resize', place);
    };
  }, [isOpen, target]);

  if (!open || !pos) return null;
  return createPortal(
    <div
      id={LIST_ID}
      role="listbox"
      aria-label="Emoji suggestions"
      style={{ position: 'fixed', left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom }}
      className={`z-[300] overflow-hidden rounded-2xl border p-1 shadow-xl backdrop-blur-xl ${
        isNight ? 'bg-[#141821]/95 border-white/10 text-white' : 'bg-white/95 border-black/10 text-black'
      }`}
    >
      {open.items.map((it, i) => (
        <div
          key={it.emoji}
          id={`${LIST_ID}-${i}`}
          role="option"
          aria-selected={i === open.active}
          // pointerdown, not click: keeps the text box focused, so the phone keyboard stays open.
          onPointerDown={e => { e.preventDefault(); target.current?.dispatchEvent(new CustomEvent('emoji-shortcode-pick', { detail: { emoji: it.emoji } })); }}
          onPointerEnter={e => { if (e.pointerType === 'mouse') setOpen({ ...open, active: i }); }}
          className={`flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer select-none ${
            i === open.active ? (isNight ? 'bg-white/10' : 'bg-black/5') : ''
          }`}
        >
          <span className="text-xl leading-none" aria-hidden="true">{it.emoji}</span>
          <span className="text-sm opacity-70 truncate">:{it.name}:</span>
        </div>
      ))}
    </div>,
    document.body,
  );
}
